import { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { Col, Form, Input, Row, Select, Typography } from 'antd';
import { RuleObject } from 'antd/es/form';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT, NAMESPACE } from '@/constants';
import { CURRENCY_OPTIONS, getCurrencySymbol } from '@/constants/currency';
import { FORM_FIELD } from './Channel.constants';
import { FORM_FIELD as LINE_ITEM_FIELD } from '../LineItem/LineItem.constants';
import { StyledRangePicker } from '../LineItem/LineItem.styles';
import { FORM_FIELD as COMMERCIAL_FIELD } from '../Commercial/Commercial.constants';
import { StyledInputNumber } from '../Commercial/Commercial.styles';
import { useChannelOptions } from '../../hooks/useChannelOptions';
import { ChannelFormContext } from '../../ChannelForm.context';
import { useGetChannelOptionsQuery, useGetDspOptionsQuery, useGetRateCardOptionsQuery } from '@/api';
import { isMeasurementChannel } from '../../../../utils/measurementChannel';
import { withSelectOption } from '../../../../utils/normalizeChannelFormPayload';

const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const COUNTRY_VALUES = [
  'UK',
  'France',
  'Poland',
  'Germany',
  'Portugal',
  'South Africa',
  'Italy',
  'Romania',
  'Spain',
  'Netherlands',
  'MENA',
  'Turkey',
  'US'
] as const;

const Channel: FC = () => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const { planCurrency, isPrefilled } = useContext(ChannelFormContext);
  const isEditing = !!isPrefilled;
  const { channel, options, totalBudget = 0, areOptionsLoading } = useChannelOptions();
  const { data: dspOptions, isLoading: areDspsLoading } = useGetDspOptionsQuery();
  const { data: channelOptionsData, isLoading: areChannelOptionsLoading } = useGetChannelOptionsQuery();
  const { data: rateCardOptions, isLoading: areRateCardOptionsLoading } = useGetRateCardOptionsQuery(
    { channel: channel ? [channel] : [], showDraft: true },
    { skip: !channel }
  );
  const form = Form.useFormInstance();
  const watchedChannel = Form.useWatch(FORM_FIELD.CHANNEL, form) as string | undefined;
  const watchedRateSource = Form.useWatch(COMMERCIAL_FIELD.RATE_SOURCE, form) as
    | 'MANUAL'
    | 'PACKAGE'
    | 'TECH_FEE'
    | undefined;
  const isPackageMode = watchedRateSource === 'PACKAGE';
  const isTechFeeMode = watchedRateSource === 'TECH_FEE';
  const isMeasurement = isMeasurementChannel(watchedChannel ?? channel);
  const selectedDspName = Form.useWatch(FORM_FIELD.DSP, form) as string | undefined;
  const hideLineDsp = isMeasurement || isPackageMode || isTechFeeMode;
  const selectedCountry = Form.useWatch(FORM_FIELD.COUNTRY, form) as string | undefined;
  const countryOptions = useMemo(
    () =>
      withSelectOption([...COUNTRY_VALUES], selectedCountry).map((country) => ({
        value: country,
        label: country
      })),
    [selectedCountry]
  );

  const canUseRateCard =
    !!channel &&
    Array.isArray((rateCardOptions as any)?.products) &&
    ((rateCardOptions as any).products?.length ?? 0) > 0;
  const monthlySpent = options?.monthlySpent?.[0];

  useEffect(() => {
    if (!channel || areRateCardOptionsLoading || isEditing) return;
    if (canUseRateCard) return;
    form.setFieldsValue({ [COMMERCIAL_FIELD.RATE_SOURCE]: 'PACKAGE' });
  }, [channel, canUseRateCard, areRateCardOptionsLoading, isEditing, form]);

  useEffect(() => {
    if (hideLineDsp) {
      form.setFieldsValue({ [FORM_FIELD.DSP]: undefined });
    }
  }, [hideLineDsp, form]);

  // Tech fee is a % of the selected line channel’s spend on the plan.
  useEffect(() => {
    if (!isTechFeeMode) return;
    const selected = watchedChannel ?? channel;
    if (selected) {
      form.setFieldsValue({ [COMMERCIAL_FIELD.FEE_BASE_CHANNEL]: selected });
    }
  }, [isTechFeeMode, watchedChannel, channel, form]);

  const validateBudget = useCallback(
    (_: RuleObject, value: number) => {
      if (monthlySpent && value < monthlySpent) {
        return Promise.reject(new Error(`The Minimum Channel Budget is ${monthlySpent}`));
      }
      return Promise.resolve();
    },
    [monthlySpent]
  );

  const selectedDspFee = useMemo(() => {
    if (!selectedDspName) return undefined;
    return dspOptions?.dsps?.find((d) => d.dspName === selectedDspName)?.feePercentage;
  }, [dspOptions?.dsps, selectedDspName]);

  const budgetCurrency = Form.useWatch(FORM_FIELD.CURRENCY, form) ?? planCurrency;

  return (
    <>
      <Row gutter={24}>
        <Col span={12}>
          <Item
            rules={[{ required: true, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
            name={FORM_FIELD.CHANNEL}
            label={t('channel')}
          >
            <Select
              size="large"
              placeholder={t('selectChannel')}
              loading={areChannelOptionsLoading}
              showSearch
              optionFilterProp="children"
            >
              {(channelOptionsData?.channels ?? []).map((opt) => (
                <Option key={opt.key} value={opt.label}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Item>
        </Col>
        <Col span={12}>
          <Item
            rules={[{ required: true, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
            name={COMMERCIAL_FIELD.RATE_SOURCE}
            label={t('rateSource')}
            initialValue="MANUAL"
          >
            <Select
              size="large"
              onChange={(value) => {
                if (value === 'PACKAGE') {
                  form.setFieldsValue({
                    [COMMERCIAL_FIELD.BUYING_METRIC]: 'CPM',
                    [COMMERCIAL_FIELD.SELL_RATE]: undefined,
                    [COMMERCIAL_FIELD.PACKAGE_ID]: undefined,
                    [COMMERCIAL_FIELD.FEE_PERCENT]: undefined,
                    [COMMERCIAL_FIELD.FEE_BASE_CHANNEL]: undefined,
                    product: undefined,
                    [LINE_ITEM_FIELD.FORMATS]: undefined,
                    [FORM_FIELD.DSP]: undefined
                  });
                } else if (value === 'TECH_FEE') {
                  form.setFieldsValue({
                    [COMMERCIAL_FIELD.BUYING_METRIC]: 'FLAT',
                    [COMMERCIAL_FIELD.SELL_RATE]: undefined,
                    [COMMERCIAL_FIELD.PACKAGE_ID]: undefined,
                    [COMMERCIAL_FIELD.BUDGET]: 0,
                    [COMMERCIAL_FIELD.FEE_BASE_CHANNEL]: form.getFieldValue(FORM_FIELD.CHANNEL),
                    [FORM_FIELD.KPI]: 'N/A',
                    product: undefined,
                    [LINE_ITEM_FIELD.FORMATS]: ['Tech Fee'],
                    [FORM_FIELD.DSP]: undefined
                  });
                } else {
                  form.setFieldsValue({
                    [COMMERCIAL_FIELD.PACKAGE_ID]: undefined,
                    [COMMERCIAL_FIELD.FEE_PERCENT]: undefined,
                    [COMMERCIAL_FIELD.FEE_BASE_CHANNEL]: undefined,
                    [LINE_ITEM_FIELD.FORMATS]: undefined,
                    product: undefined,
                    [COMMERCIAL_FIELD.SELL_RATE]: undefined
                  });
                }
              }}
            >
              <Option value="MANUAL" disabled={!canUseRateCard}>
                {t('rateCard')}
              </Option>
              <Option value="PACKAGE">{t('package')}</Option>
              <Option value="TECH_FEE">{t('techFee')}</Option>
            </Select>
          </Item>
          {channel && !canUseRateCard && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              {t('noRateCardCoverage')}
            </Text>
          )}
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12}>
          {isTechFeeMode ? (
            <Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON })
                }
              ]}
              name={COMMERCIAL_FIELD.FEE_PERCENT}
              label={t('techFee')}
              extra={t('techFeePercentHint')}
            >
              <StyledInputNumber
                min={0}
                max={100}
                size="large"
                placeholder={t('inputFeePercent')}
                addonAfter="%"
              />
            </Item>
          ) : (
            <Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON })
                },
                { validator: validateBudget, warningOnly: true }
              ]}
              name={COMMERCIAL_FIELD.BUDGET}
              label={t('budget')}
            >
              <StyledInputNumber
                disabled={!totalBudget && !monthlySpent}
                min={0}
                size="large"
                placeholder={t('inputBudget')}
                prefix={getCurrencySymbol(budgetCurrency)}
              />
            </Item>
          )}
          {isTechFeeMode && (
            <>
              <Form.Item name={COMMERCIAL_FIELD.BUDGET} hidden initialValue={0}>
                <StyledInputNumber />
              </Form.Item>
              <Form.Item name={COMMERCIAL_FIELD.FEE_BASE_CHANNEL} hidden>
                <Input />
              </Form.Item>
            </>
          )}
        </Col>
        <Col span={12}>
          <Item
            rules={[
              {
                required: true,
                message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON })
              }
            ]}
            name={LINE_ITEM_FIELD.FLIGHT_DATES}
            label={t('flightDates')}
          >
            <StyledRangePicker size="large" format={DATE_FORMAT} />
          </Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Item
            rules={[
              {
                required: true,
                message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }),
                whitespace: true
              }
            ]}
            name={FORM_FIELD.TARGETING_DETAILS}
            label={t('targetingDetails')}
          >
            <TextArea showCount maxLength={300} size="large" placeholder={t('inputYourText')} />
          </Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={8}>
          <Item
            name={FORM_FIELD.COUNTRY}
            label={t('country')}
            rules={[{ required: true, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
          >
            <Select
              size="large"
              placeholder={t('country')}
              allowClear
              options={countryOptions}
            />
          </Item>
        </Col>
        <Col span={8}>
          <Item
            name={FORM_FIELD.CURRENCY}
            label={t('currency')}
            rules={[{ required: true, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
          >
            <Select
              size="large"
              placeholder={t('currency')}
              allowClear
              options={CURRENCY_OPTIONS}
            />
          </Item>
        </Col>
        <Col span={8}>
          {isPackageMode ? (
            <Item label={t('dsp')}>
              <Text type="secondary">N/A — DSP fees are included in the package buy rate</Text>
            </Item>
          ) : isTechFeeMode ? (
            <Item label={t('dsp')}>
              <Text type="secondary">N/A</Text>
            </Item>
          ) : (
            !isMeasurement && (
              <>
                <Item
                  name={FORM_FIELD.DSP}
                  label={t('dsp')}
                  rules={[{ required: true, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
                >
                  <Select
                    size="large"
                    placeholder={t('dsp')}
                    allowClear
                    loading={areDspsLoading}
                    showSearch
                    optionFilterProp="label"
                    options={dspOptions?.dsps?.map((d) => ({
                      value: d.dspName,
                      label: d.dspName
                    })) ?? []}
                  />
                </Item>
                {selectedDspFee != null && (
                  <Text type="secondary">
                    {t('dspFee')}: {selectedDspFee}% — {t('dspFeeIncludedInRateCard')}
                  </Text>
                )}
              </>
            )
          )}
        </Col>
      </Row>
    </>
  );
};

export default Channel;
